/**
 * JSON-LD Schema Generators
 *
 * Type-safe schema generators using schema-dts for Organization and Person schemas.
 */

import type {
  Organization,
  Person,
  Article,
  FAQPage,
  BreadcrumbList,
  ListItem,
  WithContext,
} from 'schema-dts'
import type { ResearchPageData, FAQItem } from '@/data/research/types'

const PRODUCTION_URL = 'https://ncesp.org'

/**
 * Generate Organization schema for NCESP
 * Used on homepage for search engine understanding
 */
export function generateOrganizationSchema(): WithContext<Organization> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${PRODUCTION_URL}/#organization`,
    name: 'National Council for Equal Shared Parenting',
    alternateName: 'NCESP',
    url: PRODUCTION_URL,
    logo: `${PRODUCTION_URL}/logo.png`,
    foundingDate: '2024-01-01',
    founder: {
      '@type': 'Person',
      name: 'Mark Ludwig',
    },
    sameAs: ['https://www.facebook.com/profile.php?id=100066880196187'],
    taxID: '39-4627638',
    description:
      "Advocating for children's rights to maintain healthy relationships with both fit, willing, and able parents after divorce or separation.",
  }
}

/**
 * Officer data for Person schema generation
 */
export interface OfficerData {
  id: string
  name: string
  title: string
  bio: string
  socialLinks?: string[]
}

/**
 * Generate Person schema for an officer
 * Used on Leadership page for search engine understanding
 */
export function generatePersonSchema(
  officer: OfficerData
): WithContext<Person> {
  const personSchema: WithContext<Person> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${PRODUCTION_URL}/leadership#${officer.id}`,
    name: officer.name,
    jobTitle: officer.title,
    description: officer.bio,
    affiliation: {
      '@type': 'Organization',
      '@id': `${PRODUCTION_URL}/#organization`,
    },
  }

  // Add social links if available
  if (officer.socialLinks && officer.socialLinks.length > 0) {
    personSchema.sameAs = officer.socialLinks
  }

  return personSchema
}

/**
 * Generate Article schema for a research page
 */
export function generateArticleSchema(
  page: ResearchPageData
): WithContext<Article> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: page.heading,
    description: page.metaDescription,
    datePublished: page.datePublished,
    dateModified: page.dateModified,
    author: {
      '@type': 'Organization',
      name: 'National Council for Equal Shared Parenting',
      '@id': `${PRODUCTION_URL}/#organization`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'National Council for Equal Shared Parenting',
      logo: {
        '@type': 'ImageObject',
        url: `${PRODUCTION_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${PRODUCTION_URL}/research/${page.slug}`,
    },
  }
}

/**
 * Generate FAQPage schema from FAQ items
 */
export function generateFAQPageSchema(
  faqs: FAQItem[]
): WithContext<FAQPage> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question' as const,
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: faq.answer,
      },
    })),
  }
}

/**
 * Generate BreadcrumbList schema
 * Last item omits URL per Google spec (current page)
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
): WithContext<BreadcrumbList> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => {
      const isLast = index === items.length - 1
      const listItem: ListItem = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(isLast ? {} : { item: item.url }),
      }
      return listItem
    }),
  }
}
